package interviewprep.client

import androidx.compose.runtime.NoLiveLiterals
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlin.js.Promise

@NoLiveLiterals
internal fun browserTtsAvailable(): Boolean =
    window.asDynamic().speechSynthesis != undefined

@NoLiveLiterals
internal fun speak(
    text: String,
    onStart: () -> Unit = {},
    onDone: () -> Unit = {},
    onError: (String) -> Unit = {}
) {
    if (!browserTtsAvailable()) {
        onError("Text to speech is unavailable in this browser.")
        return
    }
    stopSpeaking()
    val utterance = js("new SpeechSynthesisUtterance()")
    utterance.text = text
    utterance.rate = 0.98
    utterance.onstart = {
        onStart()
    }
    utterance.onend = {
        onDone()
    }
    utterance.onerror = { event: dynamic ->
        onError("Text to speech failed: ${event.error ?: "unknown"}")
    }
    window.asDynamic().speechSynthesis.speak(utterance)
}

@NoLiveLiterals
internal fun stopSpeaking() {
    if (browserTtsAvailable()) {
        window.asDynamic().speechSynthesis.cancel()
    }
}

@NoLiveLiterals
internal fun browserSpeechRecognitionAvailable(): Boolean =
    window.asDynamic().isSecureContext == true &&
        (
            window.asDynamic().SpeechRecognition != undefined ||
                window.asDynamic().webkitSpeechRecognition != undefined
        )

internal enum class MicrophonePermissionResult(val userMessage: String) {
    GRANTED("Microphone ready."),
    UNSUPPORTED("Microphone permission needs HTTPS or localhost in a browser with microphone support."),
    DENIED("Microphone access was blocked. Allow microphone access in the browser site settings and try again.")
}

@NoLiveLiterals
internal suspend fun requestMicrophonePermission(): MicrophonePermissionResult {
    if (window.asDynamic().isSecureContext != true) {
        return MicrophonePermissionResult.UNSUPPORTED
    }
    val mediaDevices = window.navigator.asDynamic().mediaDevices ?: return MicrophonePermissionResult.UNSUPPORTED
    if (mediaDevices.getUserMedia == undefined) {
        return MicrophonePermissionResult.UNSUPPORTED
    }
    return runCatching {
        val stream = mediaDevices.getUserMedia(js("{ audio: true }")).unsafeCast<Promise<dynamic>>().await()
        val tracks = stream.getTracks()
        val trackCount = tracks.length as Int
        for (index in 0 until trackCount) {
            tracks[index].stop()
        }
        MicrophonePermissionResult.GRANTED
    }.getOrDefault(MicrophonePermissionResult.DENIED)
}

internal sealed interface SpeechRecognitionEvent {
    data object Started : SpeechRecognitionEvent
    data object AudioStarted : SpeechRecognitionEvent
    data object SoundStarted : SpeechRecognitionEvent
    data object SpeechEnded : SpeechRecognitionEvent
    data object NoMatch : SpeechRecognitionEvent
    data object Ended : SpeechRecognitionEvent
    data class Text(val value: String, val isFinal: Boolean) : SpeechRecognitionEvent
    data class Failed(val message: String) : SpeechRecognitionEvent
}

@NoLiveLiterals
internal fun startSpeechRecognition(
    onEvent: (SpeechRecognitionEvent) -> Unit
): dynamic {
    if (window.asDynamic().isSecureContext != true) {
        onEvent(SpeechRecognitionEvent.Failed("Speech recognition needs HTTPS or localhost."))
        return null
    }
    if (!browserSpeechRecognitionAvailable()) {
        onEvent(SpeechRecognitionEvent.Failed("Speech recognition is unavailable in this browser."))
        return null
    }
    val recognition = js("new (window.SpeechRecognition || window.webkitSpeechRecognition)()")
    recognition.lang = "en-US"
    recognition.continuous = true
    recognition.interimResults = true
    recognition.maxAlternatives = 1
    recognition.onstart = {
        onEvent(SpeechRecognitionEvent.Started)
    }
    recognition.onaudiostart = {
        onEvent(SpeechRecognitionEvent.AudioStarted)
    }
    recognition.onsoundstart = {
        onEvent(SpeechRecognitionEvent.SoundStarted)
    }
    recognition.onspeechend = {
        onEvent(SpeechRecognitionEvent.SpeechEnded)
    }
    recognition.onnomatch = {
        onEvent(SpeechRecognitionEvent.NoMatch)
    }
    recognition.onerror = { event: dynamic ->
        val code = event.error as? String
        val message =
            when (code) {
                "service-not-allowed" ->
                    "Chrome blocked its speech recognition service. Use a regular Chrome/Safari profile with speech recognition enabled."
                "not-allowed" ->
                    "Microphone access was blocked. Allow microphone access and try again."
                "no-speech" ->
                    "No speech was detected. Try again."
                "audio-capture" ->
                    "No microphone was found. Check your input device and try again."
                "network" ->
                    "Speech recognition could not reach the browser speech service."
                else ->
                    "Speech recognition failed: ${code ?: "unknown"}"
            }
        onEvent(SpeechRecognitionEvent.Failed(message))
    }
    recognition.onend = {
        onEvent(SpeechRecognitionEvent.Ended)
    }
    recognition.onresult = { event: dynamic ->
        runCatching {
            val results = event.results
            val resultCount = results.length as Int
            val parts = mutableListOf<String>()
            var hasFinal = false
            for (index in 0 until resultCount) {
                val item = results[index]
                val transcript = item[0].transcript as String
                if (transcript.isNotBlank()) {
                    parts.add(transcript.trim())
                }
                if (item.isFinal == true) {
                    hasFinal = true
                }
            }
            RecognizedSpeech(parts.joinToString(" ").trim(), hasFinal)
        }.onSuccess { speech ->
            if (speech.text.isNotBlank()) {
                onEvent(SpeechRecognitionEvent.Text(speech.text, speech.isFinal))
                if (speech.isFinal) {
                    runCatching { recognition.stop() }
                }
            }
        }.onFailure {
            onEvent(SpeechRecognitionEvent.Failed("Could not read speech recognition result."))
        }
    }
    recognition.start()
    return recognition
}

private data class RecognizedSpeech(
    val text: String,
    val isFinal: Boolean
)

@NoLiveLiterals
internal fun stopSpeechRecognition(handle: dynamic) {
    if (handle != null) {
        runCatching { handle.stop() }
    }
}

@NoLiveLiterals
internal fun timezoneOffsetMinutes(): Int = -(js("new Date().getTimezoneOffset()") as Int)
